import React from "react";
import { Star } from "lucide-react";
import { Reveal, ExternalLinkIcon, SectionLabel } from "./primitives";
import { trustpilot } from "../data/content";
import { track } from "../lib/analytics";

const Stars = ({ rating, size = "h-5 w-5" }) => (
  <span className="inline-flex gap-0.5" role="img" aria-label={`${rating} out of 5 stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <span
        key={i}
        className={`grid place-items-center rounded-[3px] ${i < rating ? "bg-[#00b67a]" : "bg-brand-border"}`}
      >
        <Star className={`${size} p-0.5 fill-white text-white`} aria-hidden="true" />
      </span>
    ))}
  </span>
);

export const ReviewsSection = () => (
  <section
    id="reviews"
    aria-labelledby="reviews-heading"
    className="border-t border-brand-border bg-white py-16 lg:py-24"
  >
    <div className="mx-auto max-w-content px-4 lg:px-8">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <Reveal className="max-w-xl">
          <SectionLabel>Customer reviews</SectionLabel>
          <h2
            id="reviews-heading"
            className="mt-3 font-heading text-3xl font-bold tracking-tight text-brand-navy lg:text-4xl"
          >
            What our customers say
          </h2>
          <p className="mt-3 text-lg leading-relaxed text-brand-body">
            We are proud to be rated{" "}
            <span className="font-semibold text-brand-navy">{trustpilot.label}</span> by our
            customers on Trustpilot.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="flex items-center gap-5 rounded-2xl border border-brand-border bg-brand-bg p-5">
          <div>
            <p className="font-heading text-4xl font-bold text-brand-navy">
              {trustpilot.score}
              <span className="text-xl text-brand-body">/{trustpilot.scoreOutOf}</span>
            </p>
            <p className="text-sm font-semibold text-[#00b67a]">{trustpilot.label}</p>
          </div>
          <div className="border-l border-brand-border pl-5">
            <Stars rating={5} />
            <a
              href={trustpilot.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("external_regulator_link_clicked", { label: "trustpilot" })}
              className="mt-1 block text-sm font-semibold text-brand-teal underline-offset-2 hover:underline"
              data-testid="trustpilot-count-link"
            >
              Based on {trustpilot.total}+ reviews
            </a>
          </div>
        </Reveal>
      </div>

      <div className="mt-10">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-semibold uppercase tracking-[0.14em] text-brand-teal">
            Recent reviews
          </p>
          <p className="hidden text-sm text-brand-body sm:block" aria-hidden="true">
            Scroll for more →
          </p>
        </div>
        <ul
          className="-mx-4 flex snap-x snap-mandatory gap-5 overflow-x-auto px-4 pb-4"
          data-testid="reviews-scroller"
          tabIndex={0}
          aria-label="Customer reviews — scrollable"
        >
          {trustpilot.reviews.map((r, i) => (
            <li
              key={r.name + i}
              className="flex w-[300px] flex-shrink-0 snap-start flex-col rounded-2xl border border-brand-border bg-brand-bg p-6"
              data-testid={`review-${i}`}
            >
              <Stars rating={r.rating} size="h-4 w-4" />
              <p className="mt-4 flex-1 text-base leading-relaxed text-brand-body">“{r.text}”</p>
              <div className="mt-5 flex items-center gap-3 border-t border-brand-border pt-4">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-brand-teal/10 font-heading font-bold text-brand-teal">
                  {r.name.charAt(0)}
                </span>
                <span>
                  <span className="block font-semibold text-brand-navy">{r.name}</span>
                  <span className="block text-sm text-brand-body">{r.date}</span>
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <Reveal className="mt-8">
        <a
          href={trustpilot.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("external_regulator_link_clicked", { label: "trustpilot" })}
          className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-brand-navy px-6 text-center font-semibold text-brand-navy transition-colors hover:bg-brand-navy hover:text-white"
          data-testid="trustpilot-read-all"
          aria-label="Read all Freeman Jones reviews on Trustpilot — opens in a new tab"
        >
          Read all reviews on Trustpilot <ExternalLinkIcon />
        </a>
      </Reveal>
    </div>
  </section>
);
